/*
 * DetailPanel.jsx
 *
 * Side panel that opens when a charger is selected. Owns all secondary API fetches:
 * usage (30d + 24h), work orders, charger details/flags, and OCPP telemetry.
 * Also contains the three action modals: MuteModal, AckModal, ScheduleWOModal.
 * Imports from: cards.jsx, charts.jsx, components.jsx, utils.js, @clerk/clerk-react.
 * Imported by: Dashboard.jsx (DetailPanel, MuteModal, AckModal, ScheduleWOModal),
 *              AlertsTab.jsx (DetailPanel).
 */

import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  API, getTier, getComponents, timeAgo,
} from "./utils";
import { prettyMetric, fmtHourLabel, TELEMETRY_COLORS, HealthChart, UsageChart, UsageChart24h, TelemetryChart } from "./charts";
import {
  CapacityCard, WorkOrderCard, SilentRiskCard, DiagnosisCard,
  ProactiveMaintCard, ModelConsensus, FlappingCard, RevenueCard,
  AlertRationale, ThroughputImpactCard,
} from "./cards";
import { Pill, ConfidenceBadge, ChartModal } from "./components";

// ── DetailPanel ───────────────────────────────────────────────────────────────

export const DetailPanel = ({ charger, onClose, onShowOnMap }) => {
  const { getToken } = useAuth();
  const [usageData,      setUsageData]      = useState(null);
  const [usageLoading,   setUsageLoading]   = useState(false);
  const [detailsData,    setDetailsData]    = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [woData,         setWoData]         = useState(null);
  const [flags,          setFlags]          = useState({ vandalized: false, needs_replacement: false, has_sleeve: false, ctep_tagged: false });
  const [expandedChart,  setExpandedChart]  = useState(null);
  const [usageRange,     setUsageRange]     = useState("30d");
  const [hourlyData,     setHourlyData]     = useState(null);
  const [hourlyLoading,  setHourlyLoading]  = useState(false);

  const [teleMetrics,        setTeleMetrics]        = useState([]);
  const [teleMetricsLoading, setTeleMetricsLoading] = useState(false);
  const [teleSelected,       setTeleSelected]       = useState([]);
  const [teleRange,          setTeleRange]          = useState("7d");
  const [teleStart,          setTeleStart]          = useState("");
  const [teleEnd,            setTeleEnd]            = useState("");
  const [teleSeries,         setTeleSeries]         = useState([]);
  const [teleLoading,        setTeleLoading]        = useState(false);
  const [teleError,          setTeleError]          = useState(null);

  useEffect(() => {
    if (!charger) return;
    setUsageData(null);
    setUsageLoading(true);
    setHourlyData(null);
    (async () => {
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API}/usage/${charger.charger_sn}`, { headers });
        setUsageData(r.ok ? await r.json() : null);
      } catch { } finally {
        setUsageLoading(false);
      }
    })();
  }, [charger?.charger_sn, getToken]);

  useEffect(() => {
    if (!charger || usageRange !== "24h") return;
    setHourlyLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API}/usage/${charger.charger_sn}/24h`, { headers });
        setHourlyData(r.ok ? await r.json() : null);
      } catch { } finally { setHourlyLoading(false); }
    })();
  }, [charger?.charger_sn, getToken, usageRange]);

  useEffect(() => {
    if (!charger) return;
    setDetailsData(null);
    setDetailsLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API}/charger/${charger.charger_sn}/details`, { headers });
        const d = r.ok ? await r.json() : null;
        setDetailsData(d);
        if (d?.flags) setFlags({ vandalized: false, needs_replacement: false, has_sleeve: false, ctep_tagged: false, ...d.flags });
      } catch { } finally {
        setDetailsLoading(false);
      }
    })();
  }, [charger?.charger_sn, getToken]);

  useEffect(() => {
    if (!charger) return;
    setWoData(null);
    (async () => {
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API}/charger/${charger.charger_sn}/work-orders`, { headers });
        if (r.ok) setWoData(await r.json());
      } catch { }
    })();
  }, [charger?.charger_sn, getToken]);

  useEffect(() => {
    if (!charger) return;
    setTeleMetrics([]);
    setTeleSelected([]);
    setTeleSeries([]);
    setTeleError(null);
    setTeleMetricsLoading(true);
    (async () => {
      const url = `${API}/charger/${charger.charger_sn}/telemetry/metrics`;
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(url, { headers });
        if (r.ok) {
          const d  = await r.json();
          const ms = d.metrics || [];
          setTeleMetrics(ms);
          const prefer = ms.find(m => /temp/i.test(m.key)) || ms.find(m => /power/i.test(m.key)) || ms[0];
          if (prefer) setTeleSelected([prefer.key]);
        } else {
          const body = await r.text().catch(() => "");
          const msg = `metrics request failed: HTTP ${r.status} ${r.statusText} — ${url}${body ? ` — ${body.slice(0, 200)}` : ""}`;
          console.error("[telemetry]", msg);
          setTeleError(msg);
        }
      } catch (e) {
        const msg = `metrics request error: ${e?.message || e} — ${url}`;
        console.error("[telemetry]", msg);
        setTeleError(msg);
      } finally {
        setTeleMetricsLoading(false);
      }
    })();
  }, [charger?.charger_sn, getToken]);

  useEffect(() => {
    if (!charger || teleSelected.length === 0) { setTeleSeries([]); return; }
    if (teleRange === "custom" && (!teleStart || !teleEnd)) return;
    let cancelled = false;
    setTeleLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const params = new URLSearchParams();
        params.set("metrics", teleSelected.join(","));
        if (teleRange === "custom") {
          params.set("start", new Date(`${teleStart}T00:00:00`).toISOString());
          params.set("end",   new Date(`${teleEnd}T23:59:59`).toISOString());
        } else {
          params.set("range", teleRange);
        }
        const r = await fetch(`${API}/charger/${charger.charger_sn}/telemetry?${params.toString()}`, { headers });
        if (!cancelled && r.ok) {
          const d = await r.json();
          setTeleSeries(d.series || []);
        }
      } catch { } finally {
        if (!cancelled) setTeleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [charger?.charger_sn, getToken, teleSelected, teleRange, teleStart, teleEnd]);

  const saveFlag = async (key, val) => {
    const newFlags = { ...flags, [key]: val };
    setFlags(newFlags);
    try {
      const token = await getToken();
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}), "Content-Type": "application/json" };
      await fetch(`${API}/charger/${charger.charger_sn}/flags`, {
        method: "POST", headers,
        body: JSON.stringify({ ...newFlags, updated_by: "dashboard" }),
      });
    } catch { }
  };

  if (!charger) return null;
  const tc = getTier(charger.priority_tier);
  return (
    <div className="dp-surface dp-detail" style={{
      background: "#FFFFFF", border: `1px solid #E5E7EB`,
      borderTop: `3px solid ${tc.color}`, borderRadius: 8, padding: 22,
      position: "relative", maxHeight: "calc(100vh - 48px)", overflowY: "auto",
      width: "100%",
    }}>
      <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6, alignItems: "center" }}>
        {onShowOnMap && charger.latitude && charger.longitude && (
          <button
            onClick={onShowOnMap}
            className="dp-btn-ghost"
            title="Locate on map"
            style={{
              fontSize: 11, padding: "3px 8px", borderRadius: 6,
              background: "#F9FAFB", border: "1px solid #E5E7EB",
              color: "#6B7280", cursor: "pointer", fontWeight: 600,
            }}
          >📍 Show on map</button>
        )}
        <button onClick={onClose} style={{
          background: "none", border: "none", color: "#9CA3AF",
          cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "2px 6px",
        }}>×</button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: tc.color, flexShrink: 0 }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: "#1E3A8A" }}>
            {charger.station_id && charger.station_id !== "—"
              ? charger.station_id
              : charger.charger_sn}
          </span>
          <span style={{ background: tc.bg, border: `1px solid ${tc.color}`, color: tc.color, fontSize: 10, padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>
            {charger.priority_tier}
          </span>
          <ConfidenceBadge prob={charger.fault_probability} />
        </div>
        {(charger.site_name || detailsData?.site_name) && (
          <div style={{ fontSize: 13, fontWeight: 500, color: "#374151", paddingLeft: 16, marginBottom: 3 }}>
            {charger.site_name || detailsData.site_name}
          </div>
        )}
        {detailsLoading && !detailsData ? (
          <>
            <div style={{ height: 11, background: "#F3F4F6", borderRadius: 4, width: "70%", marginBottom: 4, marginLeft: 16, animation: "pulse 1.5s ease infinite" }} />
            <div style={{ height: 10, background: "#F3F4F6", borderRadius: 4, width: "50%", marginLeft: 16, animation: "pulse 1.5s ease infinite" }} />
          </>
        ) : (
          <>
            <div style={{ fontSize: 11, color: "#9CA3AF", paddingLeft: 16, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontFamily: "'DM Mono', monospace" }}>{charger.charger_sn}</span>
              {detailsData?.vendor && (
                <span>· {detailsData.vendor}{detailsData.model ? ` ${detailsData.model}` : ""}</span>
              )}
            </div>
            {(detailsData?.address || detailsData?.county) && (
              <div style={{ fontSize: 10, color: "#9CA3AF", paddingLeft: 16, marginTop: 2 }}>
                {[detailsData.address, detailsData.county ? `${detailsData.county} County` : null].filter(Boolean).join(" · ")}
              </div>
            )}
          </>
        )}
      </div>

      {/* Status + hardware pills */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
        {detailsData?.current_status && (() => {
          const SC = { Available: "#22C55E", Charging: "#3B82F6", Faulted: "#EF4444", Unavailable: "#9CA3AF", Preparing: "#F59E0B", Finishing: "#3B82F6" };
          const sc = SC[detailsData.current_status] || "#9CA3AF";
          const sinceFmt = detailsData.status_since ? (() => {
            const d = new Date(detailsData.status_since);
            return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
              " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
          })() : null;
          return (
            <Pill color={sc}>
              <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: sc, marginRight: 5, verticalAlign: "middle" }} />
              {detailsData.current_status}{sinceFmt ? ` since ${sinceFmt}` : ""}
            </Pill>
          );
        })()}
        {charger.station_type && <Pill color="#1E3A8A">{charger.station_type}</Pill>}
        {detailsData?.connector_types?.map(ct => <Pill key={ct} color="#6B7280">{ct}</Pill>)}
        {detailsData?.fw_version && <Pill>{`FW ${detailsData.fw_version}`}</Pill>}
        {detailsData?.commissioned_at && (
          <Pill>{`Since ${new Date(detailsData.commissioned_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`}</Pill>
        )}
      </div>

      {/* Flags */}
      <div className="dp-card" style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14, padding: "8px 12px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 6 }}>
        {[
          { key: "vandalized",        label: "Vandalized",        color: "#EF4444" },
          { key: "has_sleeve",        label: "Has Sleeve",        color: "#9CA3AF" },
          { key: "needs_replacement", label: "Needs Replacement", color: "#F97316" },
          { key: "ctep_tagged",       label: "CTEP Tagged",       color: "#9CA3AF" },
        ].map(({ key, label, color }) => (
          <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={!!flags[key]}
              onChange={e => saveFlag(key, e.target.checked)}
              style={{ accentColor: color, width: 14, height: 14, cursor: "pointer" }}
            />
            <span style={{ fontSize: 11, color: flags[key] ? color : "#9CA3AF", fontWeight: flags[key] ? 600 : 400 }}>
              {label}
            </span>
          </label>
        ))}
      </div>

      <AlertRationale charger={charger} usageData={usageData} />
      <FlappingCard charger={charger} />

      {/* Uptime */}
      {charger.uptime_30d != null && (() => {
        const u      = charger.uptime_30d;
        const color  = u >= 97 ? "#16A34A" : u >= 90 ? "#D97706" : "#EF4444";
        const bg     = u >= 97 ? "#F0FDF4" : u >= 90 ? "#FFFBEB" : "#FEF2F2";
        const border = u >= 97 ? "#BBF7D0" : u >= 90 ? "#FDE68A" : "#FECACA";
        const cls    = u >= 97 ? "dp-card-success" : u >= 90 ? "dp-card-amber" : "dp-card-danger";
        const label  = u >= 97 ? "NEVI COMPLIANT" : u >= 90 ? "BELOW NEVI THRESHOLD" : "CRITICAL UPTIME";
        return (
          <div className={`dp-card ${cls}`} style={{ background: bg, border: `1px solid ${border}`, borderLeft: `3px solid ${color}`, borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 10, color, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>⏱ UPTIME (30D) — {label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{u.toFixed(1)}%</div>
            {u < 97 && (
              <div style={{ fontSize: 11, color: "#6B7280", marginTop: 6 }}>
                NEVI compliance requires ≥97% uptime. This charger may be ineligible for federal charging infrastructure funding.
              </div>
            )}
          </div>
        );
      })()}

      <CapacityCard charger={charger} />
      <SilentRiskCard charger={charger} woData={woData} />
      <ProactiveMaintCard charger={charger} />
      <DiagnosisCard charger={charger} />

      {(detailsData?.revenue_30d?.sessions > 0) && (
        <RevenueCard data30={detailsData.revenue_30d} data7={detailsData.revenue_7d} />
      )}

      <ThroughputImpactCard charger={charger} />
      <WorkOrderCard charger={charger} woData={woData} />

      {/* Usage chart */}
      {(() => {
        const daily              = usageData?.daily || [];
        const lastActiveDate     = usageData?.last_active_date;
        const isHistorical       = usageData?.is_historical;
        const daysSinceTelemetry = usageData?.days_since_last_telemetry ?? null;
        const totalAttempted     = daily.reduce((s, d) => s + (d.attempted  || 0), 0);
        const totalSuccessful    = daily.reduce((s, d) => s + (d.successful || 0), 0);
        const totalPowerLoss     = daily.reduce((s, d) => s + (d.power_loss || 0), 0);
        const successPct  = totalAttempted > 0 ? Math.min(100, Math.round(totalSuccessful / totalAttempted * 100)) : null;
        const rateColor   = successPct === null ? "#6B7280" : successPct > 80 ? "#16A34A" : successPct >= 50 ? "#D97706" : "#EF4444";
        const fmtDate     = (d) => { if (!d) return null; const dt = /T|\s\d{2}:/.test(String(d)) ? new Date(d) : new Date(d + "T12:00:00"); return isNaN(dt.getTime()) ? null : dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); };
        const hourly      = hourlyData?.hourly || [];
        const allErrors   = hourlyData?.all_errors || [];
        const tabBtnSt    = (active) => ({
          fontSize: 10, padding: "3px 9px", borderRadius: 5, cursor: "pointer", fontWeight: 600,
          border: `1px solid ${active ? "#2563EB" : "#E5E7EB"}`,
          background: active ? "#EFF6FF" : "#fff",
          color: active ? "#2563EB" : "#6B7280",
        });
        return (
          <div className="dp-card" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 6, padding: "12px 14px", marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontSize: 10, color: "#6B7280", fontWeight: 700, letterSpacing: "0.08em" }}>
                USAGE HISTORY
                {usageRange === "30d" && isHistorical && <span style={{ color: "#F97316", fontWeight: 700 }}> — HISTORICAL</span>}
                {usageRange === "30d" && !isHistorical && successPct !== null && (
                  <span style={{ color: rateColor, fontWeight: 700 }}> — {successPct}% success rate</span>
                )}
              </span>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <button style={tabBtnSt(usageRange === "24h")} onClick={() => setUsageRange("24h")}>24H</button>
                <button style={tabBtnSt(usageRange === "30d")} onClick={() => setUsageRange("30d")}>30D</button>
                {usageRange === "30d" && daily.length > 0 && (
                  <button onClick={() => setExpandedChart("usage")} style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 4, padding: "2px 7px", fontSize: 11, color: "#6B7280", cursor: "pointer" }} title="Expand">⛶</button>
                )}
                {usageRange === "24h" && hourly.length > 0 && (
                  <button onClick={() => setExpandedChart("usage24h")} style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 4, padding: "2px 7px", fontSize: 11, color: "#6B7280", cursor: "pointer" }} title="Expand">⛶</button>
                )}
              </div>
            </div>

            {usageRange === "30d" && (
              <>
                {totalPowerLoss > 0 && (
                  <div style={{ fontSize: 10, color: "#D97706", marginBottom: 8 }}>
                    ⚡ {totalPowerLoss} power loss event{totalPowerLoss !== 1 ? "s" : ""} detected — possible grid issue
                  </div>
                )}
                {usageLoading && <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>Loading usage data...</div>}
                {!usageLoading && !isHistorical && daysSinceTelemetry !== null && daysSinceTelemetry >= 3 && (
                  <div style={{ background: daysSinceTelemetry >= 7 ? "#FFF7ED" : "#FFFBEB", border: `1px solid ${daysSinceTelemetry >= 7 ? "#FED7AA" : "#FDE68A"}`, borderRadius: 6, padding: "10px 12px", fontSize: 11, color: daysSinceTelemetry >= 7 ? "#92400E" : "#92400E", marginBottom: 8 }}>
                    No telemetry received in <strong>{daysSinceTelemetry} day{daysSinceTelemetry !== 1 ? "s" : ""}</strong> — chart shows last known data.
                    {fmtDate(lastActiveDate) && <span> Last session: <span style={{ fontWeight: 700 }}>{fmtDate(lastActiveDate)}</span>.</span>}
                  </div>
                )}
                {!usageLoading && daily.length > 0 && <UsageChart data={daily} height={200} />}
                {!usageLoading && isHistorical && (
                  <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "10px 12px", fontSize: 11, color: "#92400E", marginTop: daily.length > 0 ? 10 : 0 }}>
                    No telemetry for <strong>{daysSinceTelemetry ?? 30}+ days</strong> — charger may be offline or decommissioned.
                    {fmtDate(lastActiveDate) && <span> Last session: <span style={{ fontWeight: 700 }}>{fmtDate(lastActiveDate)}</span>.</span>}
                    {" "}Verify status before dispatching.
                  </div>
                )}
                {!usageLoading && daily.length === 0 && !isHistorical && usageData && (
                  <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>No usage records found</div>
                )}
                {!usageLoading && !usageData && (
                  <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>Usage data unavailable</div>
                )}
              </>
            )}

            {usageRange === "24h" && (
              <>
                {hourlyLoading && <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>Loading 24h data...</div>}
                {!hourlyLoading && hourly.length > 0 && <UsageChart24h data={hourly} height={200} />}
                {!hourlyLoading && hourly.length === 0 && (
                  <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>No activity in the last 24 hours</div>
                )}
                {!hourlyLoading && allErrors.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, color: "#6B7280", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>ERROR LOG — LAST 24H</div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                        <thead>
                          <tr style={{ background: "#F3F4F6" }}>
                            {["Time", "Code", "Impact", "Description", "Category", "×"].map(h => (
                              <th key={h} style={{ padding: "4px 8px", textAlign: "left", color: "#6B7280", fontWeight: 700, borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[...allErrors].sort((a, b) => b.hour?.localeCompare(a.hour || "") || 0).map((e, i) => {
                            const impactCol = { H: '#EF4444', M: '#F97316', L: '#D97706', N: '#9CA3AF' }[e.impact] || '#9CA3AF';
                            return (
                              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#F9FAFB", borderBottom: "1px solid #F3F4F6" }}>
                                <td style={{ padding: "4px 8px", color: "#6B7280", whiteSpace: "nowrap" }}>{fmtHourLabel(e.hour)}</td>
                                <td style={{ padding: "4px 8px", fontWeight: 700, color: "#111827", whiteSpace: "nowrap" }}>
                                  {e.code}
                                  {e.is_precursor && <span style={{ color: "#7C3AED", fontSize: 9, marginLeft: 4 }}>⚡</span>}
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                  <span style={{ color: impactCol, fontWeight: 700 }}>{e.impact}</span>
                                </td>
                                <td style={{ padding: "4px 8px", color: "#374151", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.description}</td>
                                <td style={{ padding: "4px 8px", color: "#9CA3AF", whiteSpace: "nowrap" }}>{e.subcategory || e.category || "—"}</td>
                                <td style={{ padding: "4px 8px", color: "#6B7280", textAlign: "right" }}>{e.count}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Telemetry log */}
      {(() => {
        const selMetrics = teleSelected.map((k, i) => {
          const m = teleMetrics.find(x => x.key === k) || { key: k };
          return { key: k, unit: m.unit, label: prettyMetric(k), color: TELEMETRY_COLORS[i % TELEMETRY_COLORS.length] };
        });
        const ranges = [["24h", "24h"], ["7d", "Week"], ["custom", "Custom"]];
        const inputStyle = { fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #E5E7EB", color: "#374151", outline: "none" };
        return (
          <div className="dp-card" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 6, padding: "12px 14px", marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 10, color: "#6B7280", fontWeight: 700, letterSpacing: "0.08em" }}>TELEMETRY LOG</span>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {ranges.map(([v, lbl]) => (
                  <button key={v} onClick={() => setTeleRange(v)} style={{
                    fontSize: 10, padding: "3px 9px", borderRadius: 5, cursor: "pointer", fontWeight: 600,
                    border: `1px solid ${teleRange === v ? "#2563EB" : "#E5E7EB"}`,
                    background: teleRange === v ? "#EFF6FF" : "#fff",
                    color: teleRange === v ? "#2563EB" : "#6B7280",
                  }}>{lbl}</button>
                ))}
                {teleSeries.length > 0 && (
                  <button onClick={() => setExpandedChart("telemetry")} style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 4, padding: "2px 7px", fontSize: 11, color: "#6B7280", cursor: "pointer" }} title="Expand">⛶</button>
                )}
              </div>
            </div>

            {teleRange === "custom" && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <input type="date" value={teleStart} max={teleEnd || undefined} onChange={e => setTeleStart(e.target.value)} style={inputStyle} />
                <span style={{ fontSize: 11, color: "#9CA3AF" }}>to</span>
                <input type="date" value={teleEnd} min={teleStart || undefined} onChange={e => setTeleEnd(e.target.value)} style={inputStyle} />
              </div>
            )}

            {teleMetricsLoading ? (
              <div style={{ fontSize: 11, color: "#9CA3AF", padding: "20px 0", textAlign: "center" }}>Loading metrics…</div>
            ) : teleError ? (
              <div style={{ fontSize: 10, color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: "8px 10px", wordBreak: "break-word", fontFamily: "'DM Mono', monospace" }}>
                ⚠ Telemetry unavailable — {teleError}
                <div style={{ marginTop: 6, color: "#92400E" }}>
                  Debug: <code>{API}/charger/{charger.charger_sn}/telemetry/debug</code>
                </div>
              </div>
            ) : teleMetrics.length === 0 ? (
              <div style={{ fontSize: 11, color: "#9CA3AF", padding: "20px 0", textAlign: "center" }}>
                No telemetry metrics found for this charger.
                <div style={{ marginTop: 6, fontSize: 10, color: "#B45309", fontFamily: "'DM Mono', monospace", wordBreak: "break-word" }}>
                  Debug: <code>{API}/charger/{charger.charger_sn}/telemetry/debug</code>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 12px", marginBottom: 10 }}>
                  {teleMetrics.map(m => {
                    const idx     = teleSelected.indexOf(m.key);
                    const checked = idx >= 0;
                    const color   = checked ? TELEMETRY_COLORS[idx % TELEMETRY_COLORS.length] : "#D1D5DB";
                    return (
                      <label key={m.key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer", color: "#374151" }}>
                        <input
                          type="checkbox" checked={checked}
                          onChange={() => setTeleSelected(s => checked ? s.filter(k => k !== m.key) : [...s, m.key])}
                          style={{ cursor: "pointer", accentColor: color }}
                        />
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: color, display: "inline-block", flexShrink: 0 }} />
                        {prettyMetric(m.key)}{m.unit ? <span style={{ color: "#9CA3AF" }}> ({m.unit})</span> : null}
                      </label>
                    );
                  })}
                </div>

                {teleSelected.length === 0 ? (
                  <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>Select one or more metrics to plot</div>
                ) : teleLoading ? (
                  <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>Loading telemetry…</div>
                ) : teleSeries.length === 0 ? (
                  <div style={{ fontSize: 11, color: "#9CA3AF", padding: "24px 0", textAlign: "center" }}>No data in the selected range</div>
                ) : (
                  <TelemetryChart series={teleSeries} metrics={selMetrics} rangeMode={teleRange} height={220} />
                )}
              </>
            )}
          </div>
        );
      })()}

      {expandedChart === "usage" && usageData?.daily?.length > 0 && (
        <ChartModal title={`USAGE HISTORY — ${charger.charger_sn}`} onClose={() => setExpandedChart(null)}>
          <UsageChart data={usageData.daily} height={380} expanded />
        </ChartModal>
      )}
      {expandedChart === "usage24h" && hourlyData?.hourly?.length > 0 && (
        <ChartModal title={`USAGE — LAST 24H — ${charger.charger_sn}`} onClose={() => setExpandedChart(null)}>
          <UsageChart24h data={hourlyData.hourly} height={380} expanded />
        </ChartModal>
      )}
      {expandedChart === "telemetry" && teleSeries.length > 0 && (
        <ChartModal title={`TELEMETRY LOG — ${charger.charger_sn}`} onClose={() => setExpandedChart(null)}>
          <TelemetryChart
            series={teleSeries} rangeMode={teleRange} height={420} expanded
            metrics={teleSelected.map((k, i) => {
              const m = teleMetrics.find(x => x.key === k) || { key: k };
              return { key: k, unit: m.unit, label: prettyMetric(k), color: TELEMETRY_COLORS[i % TELEMETRY_COLORS.length] };
            })}
          />
        </ChartModal>
      )}
    </div>
  );
};

// ── ScheduleWOModal ───────────────────────────────────────────────────────────

export const ScheduleWOModal = ({ target, onClose, onConfirm }) => {
  const parts       = target?.bilstm_parts_prob ?? 0;
  const components  = target ? getComponents(target) : [];
  const defaultDesc = target ? [
    `Station ${target.station_id && target.station_id !== "—" ? target.station_id : target.charger_sn} flagged by Data Pigeon BiLSTM model with ${Math.round(parts * 100)}% probability of parts replacement within 14 days.`,
    "",
    "Recent indicators:",
    `- v6 fault probability: ${Math.round((target.fault_probability ?? 0) * 100)}%`,
    target.bilstm_fault_prob != null ? `- BiLSTM fault head: ${Math.round(target.bilstm_fault_prob * 100)}%` : null,
    target.derating_alert && target.offered_vs_healthy != null ? `- Capacity: ${Math.round(target.offered_vs_healthy * 100)}% of rated power` : null,
    target.days_since_last_wo < 999 ? `- Last work order: ${target.days_since_last_wo} days ago` : null,
    "",
    "Likely components to bring:",
    ...components.map(c => `  • ${c}`),
    "",
    `Model: BiLSTM v2 (AUC 0.85) · Scored: ${target.bilstm_scored_at || "—"}`,
  ].filter(v => v !== null).join("\n") : "";
  const [desc, setDesc] = useState(defaultDesc);
  if (!target) return null;
  return (
    <div onClick={onClose} className="dp-modal-overlay dp-themed-area" style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div onClick={e => e.stopPropagation()} className="dp-modal" style={{ background: "#fff", borderRadius: 10, padding: 24, width: "min(560px, 100%)", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "1px solid #E5E7EB" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#4F46E5", marginBottom: 2 }}>🔧 Schedule Proactive Maintenance</div>
        <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 14 }}>
          {target.station_id && target.station_id !== "—" ? target.station_id : target.charger_sn}
          {target.site_name && <span> · {target.site_name}</span>}
          <span style={{ marginLeft: 8, color: "#4F46E5", fontWeight: 600 }}>Parts: {Math.round(parts * 100)}%</span>
        </div>
        <textarea
          value={desc} onChange={e => setDesc(e.target.value)}
          style={{ width: "100%", fontSize: 11, padding: "8px 10px", borderRadius: 6, border: "1px solid #E5E7EB", outline: "none", resize: "vertical", minHeight: 200, color: "#374151", fontFamily: "'DM Mono', monospace" }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#F9FAFB", color: "#6B7280", cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onConfirm(desc)} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "none", background: "#4F46E5", color: "#fff", cursor: "pointer", fontWeight: 600 }}>Create Work Order</button>
        </div>
      </div>
    </div>
  );
};

// ── MuteModal ─────────────────────────────────────────────────────────────────

export const MuteModal = ({ target, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  if (!target) return null;
  return (
    <div onClick={onClose} className="dp-modal-overlay dp-themed-area" style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div onClick={e => e.stopPropagation()} className="dp-modal" style={{ background: "#fff", borderRadius: 10, padding: 24, width: "min(460px, 100%)", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "1px solid #E5E7EB" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 4 }}>Mute {target.charger_sn}</div>
        <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 14 }}>Hidden from alerts until unmuted. Still visible on map in gray.</div>
        <textarea
          value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Reason — e.g. Hardware replacement pending"
          autoFocus
          style={{ width: "100%", fontSize: 12, padding: "8px 10px", borderRadius: 6, border: "1px solid #E5E7EB", outline: "none", resize: "vertical", minHeight: 72, color: "#374151", fontFamily: "inherit" }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#F9FAFB", color: "#6B7280", cursor: "pointer" }}>Cancel</button>
          <button onClick={() => reason.trim() && onConfirm(reason.trim())} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "none", background: reason.trim() ? "#374151" : "#D1D5DB", color: "#fff", cursor: reason.trim() ? "pointer" : "not-allowed" }}>Mute charger</button>
        </div>
      </div>
    </div>
  );
};

// ── AckModal ──────────────────────────────────────────────────────────────────

export const AckModal = ({ target, onClose, onConfirm }) => {
  const [notes, setNotes] = useState("");
  if (!target) return null;
  const tc = getTier(target.priority_tier);
  return (
    <div onClick={onClose} className="dp-modal-overlay dp-themed-area" style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div onClick={e => e.stopPropagation()} className="dp-modal" style={{ background: "#fff", borderRadius: 10, padding: 24, width: "min(460px, 100%)", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "1px solid #E5E7EB" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 4 }}>Acknowledge {target.charger_sn}</div>
        <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 14 }}>
          <span style={{ color: tc.color, fontWeight: 700 }}>{target.priority_tier}</span>
          {" · "}{((target.fault_probability || 0) * 100).toFixed(1)}% fault prob
          {target.error_description && <span style={{ color: "#9CA3AF" }}> · {target.error_description}</span>}
        </div>
        <textarea
          value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Optional notes — e.g. Technician dispatched, ETA Thursday"
          autoFocus
          style={{ width: "100%", fontSize: 12, padding: "8px 10px", borderRadius: 6, border: "1px solid #E5E7EB", outline: "none", resize: "vertical", minHeight: 72, color: "#374151", fontFamily: "inherit" }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "1px solid #E5E7EB", background: "#F9FAFB", color: "#6B7280", cursor: "pointer" }}>Cancel</button>
          <button onClick={() => onConfirm(notes)} style={{ fontSize: 12, padding: "6px 16px", borderRadius: 6, border: "none", background: "#1E3A8A", color: "#fff", cursor: "pointer" }}>Acknowledge</button>
        </div>
      </div>
    </div>
  );
};
